import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Inbox, Filter, Search, User, Bot, Clock, AlertCircle, 
  CheckCircle, XCircle, Pause, ArrowUp, ArrowDown,
  Users, UserCheck, RefreshCw, MoreVertical, Edit, Trash2,
  Shield, ArrowRight, Target, Plus, Play, FilePlus
} from 'lucide-react';
import {
  workqueueService as defaultWorkqueueService,
  isTaskManagerUnreachableError,
  subscribeWorkQueueSync,
  ComplianceGuardrailError } from './shellWorkqueueService.js';
import { computeMergedQueueAnalytics, applyAssignmentTypeFilter, resolveQueueAssignmentBucket } from './workQueueAnalytics.js';
import { subscribeTeamQueueGoalsChanged } from '../analytics/teamQueueGoalsApi.js';
import {
  formatMemberDisplayName,
  getMemberAssigneeId,
  membersToTeamDisplayNames,
  remapAgenticRcmQueueHumanDisplayNames } from '../utils/organizationMembers.js';
import { canDeleteWorkQueueTask } from './workQueueLink.js';
import { openWorkQueueRecord } from './openWorkQueueRecord.js';
import { QueueStatusControl } from './QueueStatusControl.jsx';

function readShellOrganizationId() {
  if (typeof window === 'undefined') return '';
  const fromUrl = new URLSearchParams(window.location.search).get('organization_id');
  if (fromUrl) return fromUrl;
  return (
    localStorage.getItem('selectedOrganizationId') ||
    localStorage.getItem('organizationId') ||
    localStorage.getItem('organization_id') ||
    localStorage.getItem('selected_organization_id') ||
    ''
  );
}

function defaultShellGetApiBaseUrl() {
  if (typeof window !== 'undefined' && window.location) {
    const port = window.location.port;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' && (port === '3000' || port === '3001')) {
      return 'http://localhost:8001/api/v1';
    }
    return '/api/v1';
  }
  return '/api/v1';
}

/** FHIR CodeableConcept and plain strings — safe for React text nodes */
function toDisplayString(value, fallback = '') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    if (typeof value.text === 'string' && value.text) return value.text;
    if (Array.isArray(value.coding) && value.coding.length) {
      const parts = value.coding.map((c) => (c && (c.display || c.code)) || '').filter(Boolean);
      if (parts.length) return parts.join(', ');
    }
  }
  return fallback;
}

function assignFieldToString(value) {
  return toDisplayString(value, '');
}

function titleizeUnderscore(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function deriveItemType(item) {
  const typeKey = String(
    item?.item_type || item?.type || item?.metadata?.item_type || ''
  )
    .toLowerCase()
    .trim();
  const byType = {
    insurance_verification: 'Eligibility',
    prior_auth: 'Prior Authorization',
    prior_authorization: 'Prior Authorization',
    claims_processing: 'Claim',
    denial: 'Denial',
    charge_capture: 'Charge',
    ar_posting: 'AR Posting',
    remittance: 'Remittance',
    eligibility: 'Eligibility',
    authorization: 'Prior Authorization',
    claim: 'Claim',
    charge: 'Charge',
    payment: 'AR Posting' };
  if (typeKey && byType[typeKey]) return byType[typeKey];
  if (typeKey) return titleizeUnderscore(typeKey);

  const bySource = {
    remittance_advice: 'Remittance',
    denial_management: 'Denial',
    claims_processing: 'Claim',
    insurance_verification: 'Eligibility',
    prior_authorization: 'Prior Authorization',
    charge_capture: 'Charge',
    ar_posting: 'AR Posting',
    taskmanager: 'Task',
    request_management: 'Appointment Request',
    scheduling_waitlist: 'Waitlist',
    scheduling: 'Scheduling',
    progress_notes: 'Progress Note',
    forms: 'Form',
    treatment_plan: 'Treatment Plan' };
  const source = String(item?.source_service || item?.external_service || item?.target_service || '').toLowerCase();
  return bySource[source] || 'Work Item';
}

function getConsistentItemLabel(item) {
  const title = toDisplayString(item?.title, '') || toDisplayString(item?.item_data?.title, '');
  if (title) return title;

  const notes =
    toDisplayString(item?.notes, '') ||
    toDisplayString(item?.work_notes, '') ||
    toDisplayString(item?.description, '') ||
    toDisplayString(item?.item_data?.description, '');
  if (notes) return notes.length > 80 ? `${notes.slice(0, 80)}…` : notes;

  const rawId = toDisplayString(item?.id, '');
  if (rawId) return `Task ${rawId.slice(0, 8)}`;
  return 'Work queue task';
}

/**
 * Shared RCM work queue shell. Task Manager is the only list source of truth.
 */
export function RcmWorkQueueShell({
  user,
  getApiBaseUrl: getApiBaseUrlProp,
  leadSubtitle,
  TeamQueueGoalsComponent,
  headerSlot = null,
  organizationIds = null,
  workqueueService = defaultWorkqueueService }) {
  const getApiBaseUrl = typeof getApiBaseUrlProp === 'function' ? getApiBaseUrlProp : defaultShellGetApiBaseUrl;
  const orgIdsForFetch =
    organizationIds == null
      ? null
      : Array.isArray(organizationIds)
        ? organizationIds.map(String)
        : null;
  const orgIdsKey = useMemo(
    () => (Array.isArray(organizationIds) ? organizationIds.map(String).sort().join('|') : ''),
    [organizationIds]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const workQueueTab =
    tabParam === 'team-goals' || tabParam === 'task-manager' ? tabParam : 'queue';
  const setWorkQueueTab = (tab) => {
    if (tab === 'queue') setSearchParams({});
    else setSearchParams({ tab });
  };
  const [queueItems, setQueueItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState('member');
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    item_type: '',
    target_service: '',
    assigned_to_type: '',
    requires_human_review: null,
    source_service: '', // Filter by source service (insurance_verification, prior_authorization, etc.)
  });
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null); // { type: 'success'|'error', text: string }
  const [assignmentData, setAssignmentData] = useState({
    assigned_to_type: 'human',
    assigned_to_id: '',
    assigned_to_name: '',
    override_ai: false,
    reason: '',
    dropdownValue: '', // Value for the dropdown (user ID, 'ai', or 'unassigned')
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    source_service: 'denial_management',
    priority: 'normal',
    notes: '',
    assigned_to_type: 'unassigned',
    assigned_to_id: '',
    assigned_to_name: '',
    dropdownValue: 'unassigned' });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesItem, setNotesItem] = useState(null);
  const [notesForm, setNotesForm] = useState({ notes: '', action_taken: '' });
  const [activityEvents, setActivityEvents] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [notesModalTab, setNotesModalTab] = useState('notes'); // notes | activity

  const teamMemberDisplayNames = useMemo(
    () => membersToTeamDisplayNames(availableUsers),
    [availableUsers]
  );
  const teamNamesCacheKey = useMemo(
    () => teamMemberDisplayNames.slice().sort().join('|'),
    [teamMemberDisplayNames]
  );
  const displayQueueItems = useMemo(
    () => remapAgenticRcmQueueHumanDisplayNames(queueItems, teamMemberDisplayNames),
    [queueItems, teamNamesCacheKey]
  );

  useEffect(() => {
    workqueueService.setMockTeamMemberDisplayNames?.(teamMemberDisplayNames);
  }, [teamNamesCacheKey]);

  // Check if user is supervisor (owner, admin, or supervisor role)
  useEffect(() => {
    const checkSupervisorRole = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        if (!token) return;

        const API_BASE_URL = getApiBaseUrl();
        const response = await fetch(`${API_BASE_URL}/organizations/current`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` },
          credentials: 'include', // Include credentials for CORS
        });

        if (response.ok) {
          const data = await response.json();
          const role = data.current_user_role || data.role || 'member';
          setCurrentUserRole(role);
          try {
            localStorage.setItem('current_user_role', role);
          } catch {
            /* ignore */
          }
          
          // Supervisor roles: owner, admin, supervisor
          const supervisorRoles = ['owner', 'admin', 'supervisor'];
          setIsSupervisor(supervisorRoles.includes(role.toLowerCase()));
        }
      } catch (error) {
        console.error('Error checking supervisor role:', error);
      }
    };

    checkSupervisorRole();
  }, []);

  // Load org members for assignee labels and mock queue human names
  useEffect(() => {
    loadAvailableUsers();
  }, []);

  const loadAvailableUsers = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (!token) return;

      const organizationId =
        new URLSearchParams(window.location.search).get('organization_id') ||
        localStorage.getItem('selectedOrganizationId') ||
        localStorage.getItem('organizationId') ||
        localStorage.getItem('organization_id') ||
        localStorage.getItem('selected_organization_id');
      if (!organizationId) return;

      const API_BASE_URL = getApiBaseUrl();
      const url = `${API_BASE_URL}/organizations/current/members?organization_id=${encodeURIComponent(organizationId)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` },
        credentials: 'include' });

      if (response.ok) {
        const data = await response.json();
        const members = Array.isArray(data) ? data : (data.members || data.results || []);
        setAvailableUsers(members);
      }
    } catch (error) {
      console.error('Error loading available users:', error);
    }
  };

  useEffect(() => {
    loadAvailableServices();
  }, []);
  
  useEffect(() => {
    loadQueueItems();
  }, [filters, orgIdsKey]);
  
  const loadAvailableServices = () => {
    const services = workqueueService.getAvailableServices();
    setAvailableServices(services);
  };

  // Load stats after queue items are loaded (or org team names remap display)
  useEffect(() => {
    if (queueItems.length >= 0) {
      loadStats();
    }
  }, [queueItems, teamNamesCacheKey]);

  const loadQueueItems = async () => {
    try {
      if (Array.isArray(organizationIds) && organizationIds.length === 0) {
        setQueueItems([]);
        setStats({
          total: 0,
          assigned_to_ai: 0,
          assigned_to_human: 0,
          overdue: 0 });
        setLoading(false);
        return;
      }
      setLoading(true);
      let items = [];

      // Task Manager is the only list source. Cap wait so a hung TM cannot block the UI forever.
      // Inner getQueueItems already times out ~12s; keep shell race slightly above that.
      const OVERALL_LOAD_MS = 15000;
      const baseFilters = {
        ...filters,
        limit: 100,
        offset: 0 };
      const orgList =
        Array.isArray(orgIdsForFetch) && orgIdsForFetch.length > 0 ? orgIdsForFetch : null;

      const loadPromise = orgList
        ? Promise.all(
            orgList.map((oid) =>
              workqueueService.getQueueItems({
                ...baseFilters,
                organization_id: oid })
            )
          ).then((parts) => {
            const flat = (parts || []).flat().filter(Boolean);
            const seen = new Set();
            const deduped = [];
            for (const item of flat) {
              const key = `${item?.organization_id || ''}|${item?.id || item?.external_id || ''}`;
              if (seen.has(key)) continue;
              seen.add(key);
              deduped.push(item);
            }
            return deduped;
          })
        : workqueueService.getQueueItems(baseFilters);

      let timedOut = false;
      items = await Promise.race([
        loadPromise,
        new Promise((resolve) =>
          setTimeout(() => {
            timedOut = true;
            console.warn(
              `[WorkQueue] Load timeout after ${OVERALL_LOAD_MS}ms — Task Manager did not finish in time`
            );
            resolve([]);
          }, OVERALL_LOAD_MS)
        ),
      ]);

      if (timedOut) {
        setSyncMessage({
          type: 'error',
          text: 'Work Queue timed out waiting for Task Manager. Showing an empty list — retry shortly.' });
        setTimeout(() => setSyncMessage(null), 6000);
      }

      // Apply source_service filter if set
      if (filters.source_service) {
        items = items.filter(item => {
          const sourceService = item.source_service || item.external_service || item.target_service;
          return sourceService === filters.source_service ||
                 sourceService?.toLowerCase() === filters.source_service.toLowerCase();
        });
      }

      // Assignment filter must match the Assignee column (AI / Human / Unassigned).
      items = applyAssignmentTypeFilter(items, filters.assigned_to_type);

      // Preserve existing assignments; do not invent Kai labels for empty queues
      // Closed/completed/cancelled never appear in the active work queue
      const itemsWithAssignments = (items || []).filter((item) => {
        const st = String(item?.status || '').toLowerCase();
        return st !== 'completed' && st !== 'closed' && st !== 'cancelled';
      });

      setQueueItems(itemsWithAssignments);
    } catch (error) {
      console.error('[WorkQueue] Error loading queue items:', error);
      setQueueItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await workqueueService.getQueueStats();
      // Calculate stats from queue items if backend doesn't provide them
      const calculatedStats = calculateStatsFromItems(displayQueueItems, statsData);
      setStats(calculatedStats);
    } catch (error) {
      if (!isTaskManagerUnreachableError(error)) {
        console.warn('[WorkQueue] Error loading stats, calculating from items:', error.message);
      }
      // Calculate stats from queue items as fallback
      try {
        const calculatedStats = calculateStatsFromItems(displayQueueItems, null);
        setStats(calculatedStats);
      } catch (calcError) {
        console.error('[WorkQueue] Error calculating stats:', calcError);
        // Set default stats as last resort
        setStats({
          totalItems: displayQueueItems.length || 0,
          pendingItems: 0,
          inProgressItems: 0,
          completedToday: 0,
          assignedToAI: 0,
          assignedToHuman: 0,
          overdueItems: 0,
          avgProcessingTime: 0 });
      }
    }
  };

  // Calculate stats from queue items (merged real data — status, assignee, aging, throughput)
  const calculateStatsFromItems = (items, backendStats) => {
    const analytics = computeMergedQueueAnalytics(items || []);
    return {
      ...backendStats,
      total: analytics.total,
      assigned_to_ai: analytics.assigned_to_ai,
      assigned_to_human: analytics.assigned_to_human,
      unassigned: analytics.unassigned,
      overdue: analytics.overdue,
      open_count: analytics.open_count,
      working_count: analytics.working_count,
      closed_count: analytics.closed_count,
      completed_today: analytics.completed_today,
      by_status: analytics.by_status,
      by_priority: analytics.by_priority,
      aging: analytics.aging,
      top_assignees: analytics.top_assignees };
  };

  useEffect(() => {
    return subscribeWorkQueueSync(() => {
      loadQueueItems();
    });
  }, [filters, orgIdsKey]);

  useEffect(() => {
    return subscribeTeamQueueGoalsChanged(() => {
      loadStats();
    });
  }, [orgIdsKey]);

  // Keep stats in sync whenever the visible merged queue changes
  useEffect(() => {
    setStats(calculateStatsFromItems(displayQueueItems, null));
  }, [displayQueueItems]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleOpenAssignmentModal = (itemIds) => {
    // Get details of selected items (display row = remapped assignee names)
    const items = displayQueueItems.filter((item) => itemIds.includes(item.id));
    setSelectedItemDetails(items);
    setSelectedItems(itemIds);
    
    // Pre-populate assignment data with current assignment if single item
    if (items.length === 1) {
      const item = items[0];
      const assignedToStr = assignFieldToString(item.assigned_to);
      const assignedToType = item.assigned_to_type ||
        (item.auto_assigned ? 'ai' : null) ||
        (assignedToStr && assignedToStr.toLowerCase().includes('ai') ? 'ai' : 'human');

      let dropdownValue = '';
      if (assignedToType === 'ai') {
        dropdownValue = 'ai';
      } else if (assignedToType === 'human') {
        const displayName = assignFieldToString(item.assigned_to_name);
        const user = availableUsers.find((u) => {
          const id = getMemberAssigneeId(u);
          const label = formatMemberDisplayName(u);
          return label === displayName || id === assignedToStr || id === item.assigned_to;
        });
        dropdownValue = user ? getMemberAssigneeId(user) : assignedToStr || (typeof item.assigned_to === 'string' ? item.assigned_to : '');
      } else {
        dropdownValue = 'unassigned';
      }

      setAssignmentData({
        assigned_to_type: assignedToType || 'human',
        assigned_to_id:
          dropdownValue && dropdownValue !== 'ai' && dropdownValue !== 'unassigned'
            ? dropdownValue
            : assignedToStr || (typeof item.assigned_to === 'string' ? item.assigned_to : ''),
        assigned_to_name:
          assignFieldToString(item.assigned_to_name) ||
          assignFieldToString(item.assigned_to) ||
          (assignedToType === 'ai' ? 'Kai - AI Coworker' : ''),
        override_ai: item.auto_assigned || false,
        reason: '',
        dropdownValue: dropdownValue || (assignedToType === 'ai' ? 'ai' : 'unassigned') });
    } else {
      setAssignmentData({
        assigned_to_type: 'human',
        assigned_to_id: '',
        assigned_to_name: '',
        override_ai: false,
        reason: '',
        dropdownValue: '' });
    }
    
    setShowAssignmentModal(true);
  };

  const handleAssign = async () => {
    try {
      const items =
        selectedItemDetails?.length
          ? selectedItemDetails
          : displayQueueItems.filter((item) => selectedItems.includes(item.id));
      const allAssignable = items.length > 0 && items.every((item) => canReassign(item));
      if (!isSupervisor && !allAssignable) {
        alert('Only supervisors can reassign items that already have an assignee.');
        return;
      }

      // Prepare assignment data
      const assignmentPayload = {
        assigned_to: assignmentData.assigned_to_id || assignmentData.assigned_to_name || 
          (assignmentData.assigned_to_type === 'ai' ? 'ai-agent-default' : ''),
        assigned_by: user?.id || user?.user_id || 'current-user',
        assigned_to_type: assignmentData.assigned_to_type,
        assigned_to_name: assignmentData.assigned_to_name,
        override_ai: assignmentData.override_ai || selectedItemDetails?.some(item => item.auto_assigned),
        reason: assignmentData.reason,
        preserve_work: true, // Always preserve work progress
      };

      if (!assignmentPayload.assigned_to) {
        alert('Select Kai or a team member to assign.');
        return;
      }

      if (selectedItems.length === 1) {
        // Get the source service from the selected item
        const selectedItem = items[0] || displayQueueItems.find((item) => item.id === selectedItems[0]);
        const sourceService = selectedItem?.source_service || selectedItem?.external_service;
        
        await workqueueService.assignQueueItem(
          selectedItems[0],
          assignmentPayload,
          sourceService,
          selectedItem
        );
      } else if (selectedItems.length > 1) {
        const itemsById = Object.fromEntries(
          items.map((i) => [i.id, i])
        );
        await workqueueService.bulkAssignQueueItems(
          {
            queue_item_ids: selectedItems,
            ...assignmentPayload },
          itemsById
        );
      }
      
      setShowAssignmentModal(false);
      setSelectedItems([]);
      setSelectedItemDetails(null);
      setAssignmentData({
        assigned_to_type: 'human',
        assigned_to_id: '',
        assigned_to_name: '',
        override_ai: false,
        reason: '',
        dropdownValue: '' });
      loadQueueItems();
      loadStats();
      if (notesItem && selectedItems.includes(notesItem.id)) {
        loadItemActivity(notesItem);
      }
    } catch (error) {
      console.error('Error assigning items:', error);
      if (error instanceof ComplianceGuardrailError || error?.name === 'ComplianceGuardrailError') {
        setSyncMessage({
          type: 'error',
          text: `Compliance guardrail blocked Kai assignment: ${error.message}. Route to human review in RcmCompliance.` });
        return;
      }
      alert(error?.response?.data?.detail || error?.message || 'Failed to assign items. Please try again.');
    }
  };

  const handleStatusChange = async (itemId, newStatus, extra = {}) => {
    try {
      const item = displayQueueItems.find((row) => row.id === itemId);
      const sourceService = item?.source_service || item?.external_service;

      // Optimistic UI so Open / Working / Closed feel immediate
      // Closed items leave the active queue (do not keep showing them)
      const closed = ['completed', 'closed', 'cancelled'].includes(String(newStatus || '').toLowerCase());
      setQueueItems((prev) =>
        closed
          ? (prev || []).filter((row) => row.id !== itemId)
          : (prev || []).map((row) =>
              row.id === itemId
                ? {
                    ...row,
                    status: newStatus,
                    updated_at: new Date().toISOString() }
                : row
            )
      );

      await workqueueService.updateQueueItem(
        itemId,
        { status: newStatus, ...extra },
        sourceService,
        item
      );
      loadQueueItems();
      loadStats();
      if (notesItem?.id === itemId) {
        loadItemActivity(notesItem);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      loadQueueItems();
      if (error instanceof ComplianceGuardrailError || error?.name === 'ComplianceGuardrailError') {
        setSyncMessage({
          type: 'error',
          text: `Compliance guardrail blocked autonomous action: ${error.message}` });
        return;
      }
      alert(error?.response?.data?.detail || error?.message || 'Failed to update status');
    }
  };

  const handleOpenRecord = useCallback((item) => {
    const org =
      (item && item.organization_id) ||
      (Array.isArray(orgIdsForFetch) && orgIdsForFetch[0]) ||
      readShellOrganizationId();
    openWorkQueueRecord(item, org);
  }, [orgIdsForFetch]);

  /** UI lifecycle: Pending → pending/assigned, Working → in_progress, Closed → completed */
  const applyLifecycle = async (item, lifecycle) => {
    const assigned =
      assignFieldToString(item?.assigned_to) || assignFieldToString(item?.assigned_to_name);
    const assignedNorm = String(assigned || '').trim().toLowerCase();
    const hasAssignee = assignedNorm && assignedNorm !== 'unassigned' && assignedNorm !== '—' && assignedNorm !== '-';
    const map = {
      open: hasAssignee ? 'assigned' : 'pending',
      working: 'in_progress',
      closed: 'completed' };
    const status = map[lifecycle];
    if (!status) return;
    await handleStatusChange(item.id, status);
  };

  const loadItemActivity = async (item) => {
    if (!item?.id) {
      setActivityEvents([]);
      return;
    }
    setActivityLoading(true);
    try {
      const events = await workqueueService.getQueueItemActivity(item.id);
      setActivityEvents(Array.isArray(events) ? events : []);
    } catch (e) {
      console.warn('[WorkQueue] activity load failed', e);
      setActivityEvents([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const openNotesModal = (item) => {
    setNotesItem(item);
    setNotesForm({
      notes: item?.notes || '',
      action_taken: item?.action_taken || '' });
    setNotesModalTab('activity');
    setShowNotesModal(true);
    loadItemActivity(item);
  };

  const handleSaveNotes = async () => {
    if (!notesItem) return;
    try {
      await workqueueService.updateQueueItem(
        notesItem.id,
        {
          notes: notesForm.notes ?? '',
          action_taken: notesForm.action_taken ?? '' },
        notesItem.source_service || notesItem.external_service,
        notesItem
      );
      await loadItemActivity(notesItem);
      setNotesModalTab('activity');
      setSyncMessage({ type: 'success', text: 'Notes saved. Activity log updated.' });
      setTimeout(() => setSyncMessage(null), 4000);
      loadQueueItems();
    } catch (error) {
      console.error('Error saving notes:', error);
      if (error instanceof ComplianceGuardrailError || error?.name === 'ComplianceGuardrailError') {
        setSyncMessage({
          type: 'error',
          text: `Compliance guardrail blocked action: ${error.message}` });
        return;
      }
      alert(error?.response?.data?.detail || error?.message || 'Failed to save notes');
    }
  };

  const handleCreateTask = async () => {
    const title = (createForm.title || '').trim();
    if (!title) {
      alert('Title is required');
      return;
    }
    setCreateBusy(true);
    try {
      // Always mark shell-created tasks as Work Queue–only so domain apps
      // (e.g. Denial claim detail) do not treat them as Core Claims ids.
      const rawId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : String(Date.now());
      const domainId = `manual-${rawId}`;
      const src = createForm.source_service || 'denial_management';
      const serviceMap = {
        denial_management: {
          type: 'denial',
          target_service: 'denials',
          external_prefix: 'denial_mgmt' },
        ar_posting: {
          type: 'ar_posting',
          target_service: 'ar_posting',
          external_prefix: 'ar_posting' },
        insurance_verification: {
          type: 'insurance_verification',
          target_service: 'insurance_verification',
          external_prefix: 'ins_ver' },
        prior_authorization: {
          type: 'prior_auth',
          target_service: 'prior_auth',
          external_prefix: 'prior_auth' },
        claims_processing: {
          type: 'claims_processing',
          target_service: 'claims_processing',
          external_prefix: 'claims' },
        charge_capture: {
          type: 'charge_capture',
          target_service: 'charge_capture',
          external_prefix: 'charge' },
        remittance_advice: {
          type: 'remittance',
          target_service: 'remittance_advice',
          external_prefix: 'remittance' },
        patient_billing: {
          type: 'patient_billing',
          target_service: 'patient_billing',
          external_prefix: 'patient_billing' },
        contract_management: {
          type: 'contract_management',
          target_service: 'contract_management',
          external_prefix: 'contract' },
        rcm_compliance: {
          type: 'rcm_compliance',
          target_service: 'rcm_compliance',
          external_prefix: 'compliance' } };
      const m = serviceMap[src];
      if (!m) {
        alert(`Unsupported service for Create Task: ${src}`);
        setCreateBusy(false);
        return;
      }
      const externalId = `${m.external_prefix}-${domainId}`;
      const hasAssignee =
        createForm.assigned_to_type === 'ai' ||
        (createForm.assigned_to_type === 'human' && createForm.assigned_to_id);

      const itemData = {
        title,
        description: createForm.description || '',
      };
      const body = {
        type: m.type,
        external_id: externalId,
        external_service: src,
        item_data: itemData,
        item_metadata: {
          domain_id: domainId,
          source_service: src,
          display_queue_id: null,
          created_via: 'shell_create_task',
          assigned_to_type: hasAssignee ? createForm.assigned_to_type : undefined,
          assigned_to_name:
            createForm.assigned_to_type === 'ai'
              ? 'Kai - AI Coworker'
              : createForm.assigned_to_name || undefined,
        },
        priority: createForm.priority || 'normal',
        status: hasAssignee ? 'assigned' : 'pending',
        target_service: m.target_service,
        notes: createForm.notes || undefined,
        organization_id:
          (Array.isArray(orgIdsForFetch) && orgIdsForFetch[0]) ||
          undefined,
      };

      const created = await workqueueService.createQueueItem(body);

      // Close immediately after create succeeds — assign/writeback/reload must not keep the modal open.
      setShowCreateModal(false);
      setCreateForm({
        title: '',
        description: '',
        source_service: 'denial_management',
        priority: 'normal',
        notes: '',
        assigned_to_type: 'unassigned',
        assigned_to_id: '',
        assigned_to_name: '',
        dropdownValue: 'unassigned' });
      setCreateBusy(false);

      let assignWarning = null;
      if (created?.id && hasAssignee) {
        try {
          await workqueueService.assignQueueItem(
            created.id,
            {
              assigned_to:
                createForm.assigned_to_type === 'ai'
                  ? 'ai-agent-default'
                  : createForm.assigned_to_id,
              assigned_by: user?.id || user?.user_id || 'shell',
              assigned_to_type: createForm.assigned_to_type,
              assigned_to_name:
                createForm.assigned_to_type === 'ai'
                  ? 'Kai - AI Coworker'
                  : createForm.assigned_to_name,
              reason: 'Created with assignee' },
            src,
            {
              ...created,
              source_service: src,
              metadata: { domain_id: domainId } }
          );
        } catch (assignErr) {
          console.warn('[WorkQueue] Task created but assign/writeback failed:', assignErr);
          assignWarning =
            assignErr?.response?.data?.detail ||
            assignErr?.message ||
            'Assignment sync failed; you can assign from the queue.';
        }
      }

      setSyncMessage({
        type: assignWarning ? 'error' : 'success',
        text: assignWarning
          ? `Task created. ${assignWarning}`
          : 'Task created and synced to the owning service.' });
      setTimeout(() => setSyncMessage(null), 5000);

      // Refresh in background; do not block the create UX on TM reload.
      loadQueueItems().catch((err) => console.warn('[WorkQueue] Reload after create failed:', err));
      loadStats().catch((err) => console.warn('[WorkQueue] Stats reload after create failed:', err));
    } catch (error) {
      console.error('Error creating task:', error);
      alert(error?.response?.data?.detail || error?.message || 'Failed to create task');
      setCreateBusy(false);
    }
  };

  const getStatusIcon = (status) => {
    const s = typeof status === 'string' ? status : toDisplayString(status, '');
    switch (s) {
      case 'pending':
        return <Clock className="w-4 h-4 text-gray-500" />;
      case 'assigned':
        return <UserCheck className="w-4 h-4 text-blue-500" />;
      case 'in_progress':
        return <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'on_hold':
        return <Pause className="w-4 h-4 text-orange-500" />;
      default:
        return <Inbox className="w-4 h-4 text-gray-400" />;
    }
  };

  const getPriorityColor = (priority) => {
    const p = typeof priority === 'string' ? priority : toDisplayString(priority, 'normal');
    switch (p) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'urgent':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'high':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'normal':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getServiceBadgeColor = (service) => {
    const colors = {
      denials: 'bg-red-100 text-red-800',
      denial_management: 'bg-red-100 text-red-800',
      claims_processing: 'bg-blue-100 text-blue-800',
      insurance: 'bg-green-100 text-green-800',
      insurance_verification: 'bg-green-100 text-green-800',
      prior_auth: 'bg-purple-100 text-purple-800',
      prior_authorization: 'bg-purple-100 text-purple-800',
      charge_capture: 'bg-yellow-100 text-yellow-800',
      ar_posting: 'bg-indigo-100 text-indigo-800',
      remittance_advice: 'bg-cyan-100 text-cyan-800',
      patient_billing: 'bg-emerald-100 text-emerald-800',
      contract_management: 'bg-slate-100 text-slate-800',
      rcm_compliance: 'bg-amber-100 text-amber-800',
      request_management: 'bg-teal-100 text-teal-800',
      scheduling_waitlist: 'bg-cyan-100 text-cyan-800',
      scheduling: 'bg-cyan-100 text-cyan-800',
      progress_notes: 'bg-violet-100 text-violet-800',
      forms: 'bg-fuchsia-100 text-fuchsia-800',
      treatment_plan: 'bg-orange-100 text-orange-800',
      taskmanager: 'bg-slate-100 text-slate-800' };
    return colors[service] || 'bg-gray-100 text-gray-800';
  };

  const canReassign = (item) => {
    if (isSupervisor) return true;
    return !assignFieldToString(item.assigned_to) && !assignFieldToString(item.assigned_to_name);
  };

  const canDeleteTask = canDeleteWorkQueueTask(currentUserRole);

  const handleDeleteTask = async (item) => {
    if (!canDeleteTask) {
      alert('Only organization owner or admin can delete tasks.');
      return;
    }
    const label = getConsistentItemLabel(item);
    if (!window.confirm(`Delete task "${label}"? This cannot be undone.`)) return;
    try {
      await workqueueService.deleteQueueItem(item.id, { actor_role: currentUserRole });
      setQueueItems((prev) => (prev || []).filter((row) => row.id !== item.id));
      setSyncMessage({ type: 'success', text: 'Task deleted.' });
    } catch (error) {
      alert(error?.response?.data?.detail || error?.message || 'Failed to delete task');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Work Queue</h1>
            <p className="mt-2 text-sm text-gray-600 max-w-3xl">
              {leadSubtitle}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              <span>Create Task</span>
            </button>
            {headerSlot}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => setWorkQueueTab('queue')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              workQueueTab === 'queue'
                ? 'bg-blue-600 text-white shadow'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Inbox className="w-4 h-4" />
            Inbox
          </button>
          <button
            type="button"
            onClick={() => setWorkQueueTab('team-goals')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              workQueueTab === 'team-goals'
                ? 'bg-blue-600 text-white shadow'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Target className="w-4 h-4" />
            Team queue goals
          </button>
          <button
            type="button"
            onClick={() => setWorkQueueTab('task-manager')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              workQueueTab === 'task-manager'
                ? 'bg-blue-600 text-white shadow'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <UserCheck className="w-4 h-4" />
            Task Manager
          </button>
        </div>
      </div>

      {workQueueTab === 'team-goals' ? (
        <TeamQueueGoalsComponent embedded />
      ) : (
        <>
      {workQueueTab === 'task-manager' && (
        <div className="bg-white rounded-lg shadow border border-gray-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Task Manager</h2>
            <p className="text-sm text-gray-600 mt-1">
              Create work items stored in Task Manager. They show in this list and on Inbox.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            <span>New Task</span>
          </button>
        </div>
      )}
      {/* Sync success/error message - in-app, no browser dialog */}
      {syncMessage && (
        <div
          className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm ${
            syncMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          <span className="flex items-center gap-2">
            {syncMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
            )}
            {syncMessage.text}
          </span>
          <button
            type="button"
            onClick={() => setSyncMessage(null)}
            className="shrink-0 font-medium hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stats Cards — from merged real queue items */}
      {(() => {
        const displayStats = stats || calculateStatsFromItems(displayQueueItems, null);
        const totalItems = displayStats.total ?? displayQueueItems.length;
        const byStatus = displayStats.by_status || {};
        const aging = displayStats.aging || {};

        return (
          <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{totalItems}</p>
                </div>
                <Inbox className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Open</p>
                  <p className="text-2xl font-bold text-slate-700">{displayStats.open_count || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-slate-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Working</p>
                  <p className="text-2xl font-bold text-amber-600">{displayStats.working_count || 0}</p>
                </div>
                <RefreshCw className="w-8 h-8 text-amber-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Closed today</p>
                  <p className="text-2xl font-bold text-emerald-600">{displayStats.completed_today || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Kai</p>
                  <p className="text-2xl font-bold text-blue-600">{displayStats.assigned_to_ai || 0}</p>
                </div>
                <Bot className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Team / Overdue</p>
                  <p className="text-2xl font-bold text-green-600">
                    {displayStats.assigned_to_human || 0}
                    <span className="text-base text-red-600 font-semibold ml-1">
                      / {displayStats.overdue || 0}
                    </span>
                  </p>
                </div>
                <Users className="w-8 h-8 text-green-400" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm font-semibold text-gray-800 mb-2">By status</p>
              <div className="space-y-1 text-sm text-gray-700">
                {['pending', 'assigned', 'in_progress', 'completed'].map((k) => (
                  <div key={k} className="flex justify-between">
                    <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                    <span className="font-medium">{byStatus[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm font-semibold text-gray-800 mb-2">Aging (days)</p>
              <div className="space-y-1 text-sm text-gray-700">
                {['0-7', '8-14', '15-30', '30+'].map((k) => (
                  <div key={k} className="flex justify-between">
                    <span>{k}</span>
                    <span className="font-medium">{aging[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm font-semibold text-gray-800 mb-2">Top assignees</p>
              {(displayStats.top_assignees || []).length === 0 ? (
                <p className="text-sm text-gray-500">No assignees yet</p>
              ) : (
                <div className="space-y-1 text-sm text-gray-700">
                  {(displayStats.top_assignees || []).slice(0, 5).map((row) => (
                    <div key={row.label} className="flex justify-between gap-2">
                      <span className="truncate">{row.label}</span>
                      <span className="font-medium shrink-0">{row.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </h2>
          <button
            onClick={loadQueueItems}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="on_hold">On Hold</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
            <option value="critical">Critical</option>
          </select>

          <select
            value={filters.item_type}
            onChange={(e) => handleFilterChange('item_type', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Types</option>
            <option value="claim">Claim</option>
            <option value="denial">Denial</option>
            <option value="prior_auth">Prior Auth</option>
            <option value="eligibility">Eligibility</option>
            <option value="charge_capture">Charge Capture</option>
            <option value="ar_posting">AR Posting</option>
          </select>

          <select
            value={filters.source_service}
            onChange={(e) => handleFilterChange('source_service', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Source Services</option>
            {availableServices.map(service => (
              <option key={service.key} value={service.key}>
                {service.name}
              </option>
            ))}
          </select>

          <select
            value={filters.target_service}
            onChange={(e) => handleFilterChange('target_service', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Target Services</option>
            <option value="denials">Denials</option>
            <option value="claims_processing">Claims Processing</option>
            <option value="insurance_verification">Insurance Verification</option>
            <option value="prior_auth">Prior Auth</option>
            <option value="charge_capture">Charge Capture</option>
            <option value="ar_posting">AR Posting</option>
            <option value="remittance_advice">Remittance Advice</option>
            <option value="patient_billing">Patient Billing</option>
            <option value="contract_management">Contract Management</option>
            <option value="rcm_compliance">RCM Compliance</option>
          </select>

          <select
            value={filters.assigned_to_type}
            onChange={(e) => handleFilterChange('assigned_to_type', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Assignments</option>
            <option value="human">Human</option>
            <option value="ai">AI</option>
            <option value="unassigned">Unassigned</option>
          </select>

          <select
            value={filters.requires_human_review === null ? '' : filters.requires_human_review}
            onChange={(e) => handleFilterChange('requires_human_review', e.target.value === '' ? null : e.target.value === 'true')}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Items</option>
            <option value="true">Requires Review</option>
            <option value="false">No Review Needed</option>
          </select>
        </div>
      </div>

      {/* Actions Bar */}
      {selectedItems.length > 0 && (() => {
        const selectedRows = displayQueueItems.filter((item) => selectedItems.includes(item.id));
        const canBulkAssign =
          isSupervisor || (selectedRows.length > 0 && selectedRows.every((item) => canReassign(item)));
        return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm text-blue-800">
            {selectedItems.length} item(s) selected
            {!canBulkAssign && (
              <span className="ml-2 text-xs text-orange-600">
                (Supervisor required to reassign claimed items)
              </span>
            )}
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => handleOpenAssignmentModal(selectedItems)}
              disabled={!canBulkAssign}
              className={`px-4 py-2 rounded-md text-sm ${
                canBulkAssign
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {selectedItems.length === 1 ? 'Assign' : 'Assign Selected'}
            </button>
            <button
              onClick={() => setSelectedItems([])}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
            >
              Clear Selection
            </button>
          </div>
        </div>
        );
      })()}

      {/* Queue Items Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-gray-500">Loading queue items...</p>
          </div>
        ) : displayQueueItems.length === 0 ? (
          <div className="p-8 text-center">
            <Inbox className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No queue items found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[1240px] table-fixed divide-y divide-gray-200">
              <colgroup>
                <col className="w-10" />
                <col className="w-[22%]" />
                <col className="w-[10%]" />
                <col className="w-[8%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
                <col className="w-[14%]" />
                <col className="w-[8%]" />
                <col className="w-[14%]" />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedItems.length === displayQueueItems.length && displayQueueItems.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedItems(displayQueueItems.map((item) => item.id));
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayQueueItems.map((item) => {
                  const canReassignItem = canReassign(item);
                  const statusStr = toDisplayString(item.status, '');
                  const isInProgress = statusStr === 'in_progress';
                  
                  return (
                    <tr
                      key={item.id}
                      onClick={() => handleOpenRecord(item)}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedItems.includes(item.id) ? 'bg-blue-50' : ''} ${
                        isInProgress && isSupervisor ? 'border-l-4 border-yellow-400' : ''
                      }`}
                    >
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems([...selectedItems, item.id]);
                            } else {
                              setSelectedItems(selectedItems.filter(id => id !== item.id));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          className="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline break-words text-left"
                          title="Open record"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenRecord(item);
                          }}
                        >
                          {getConsistentItemLabel(item)}
                        </button>
                        {(item.notes || item.work_notes || item.description) ? (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {item.notes || item.work_notes || item.description}
                          </div>
                        ) : null}
                        {item.requires_human_review && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Review Required
                          </span>
                        )}
                        {isInProgress && isSupervisor && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mt-1 ml-1">
                            In Progress - Can Reassign
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{deriveItemType(item)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(item.priority || 'normal')}`}>
                          {toDisplayString(item.priority, 'N/A')}
                        </span>
                      </td>
                      <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                        <QueueStatusControl
                          status={statusStr}
                          onChange={(lifecycle) => applyLifecycle(item, lifecycle)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getServiceBadgeColor(item.source_service || item.external_service || item.target_service || 'unknown')}`}>
                          {item.source_service_name || 
                           (item.source_service ? item.source_service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : null) ||
                           (item.external_service ? item.external_service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : null) ||
                           (item.target_service ? item.target_service.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : null) ||
                           'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm">
                          {(() => {
                            const assignedToType = resolveQueueAssignmentBucket(item);
                            const assignedToName =
                              assignFieldToString(item.assigned_to_name) ||
                              assignFieldToString(item.assigned_to);

                            if (assignedToType === 'ai') {
                              return (
                                <>
                                  <Bot className="w-4 h-4 text-blue-500 mr-1" />
                                <span className="text-gray-900">
                                  {assignedToName || 'Kai - AI Coworker'}
                                </span>
                                </>
                              );
                            } else if (assignedToType === 'human') {
                              return (
                                <>
                                  <User className="w-4 h-4 text-green-500 mr-1" />
                                  <span className="text-gray-900">
                                    {assignedToName || 'Human'}
                                  </span>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <span className="text-gray-400 mr-1">—</span>
                                  <span className="text-gray-500">Unassigned</span>
                                </>
                              );
                            }
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {(item.created_at || item.updated_at)
                          ? new Date(item.created_at || item.updated_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td
                        className="sticky right-0 bg-white px-3 py-4 text-sm font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-wrap gap-1 min-w-[10rem]">
                            <button
                              type="button"
                              onClick={() => handleOpenRecord(item)}
                              className="px-2 py-1 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700"
                              title="Open record details"
                            >
                              Open
                            </button>
                            <button
                              onClick={() => handleOpenAssignmentModal([item.id])}
                              disabled={!canReassignItem}
                              className={`px-2 py-1 rounded-md text-xs font-medium flex items-center transition-colors ${
                                !canReassignItem
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : isInProgress && isSupervisor
                                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                                  : 'bg-white text-blue-700 border border-blue-300 hover:bg-blue-50'
                              }`}
                              title={
                                !canReassignItem
                                  ? 'Already assigned — supervisor can override'
                                  : isInProgress && isSupervisor
                                  ? 'Override Assignment (Supervisor)'
                                  : 'Assign'
                              }
                            >
                              {isInProgress && isSupervisor ? (
                                <>
                                  <Shield className="w-3 h-3 mr-1" />
                                  Override
                                </>
                              ) : (
                                <>
                                  <UserCheck className="w-3 h-3 mr-1" />
                                  Assign
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => openNotesModal(item)}
                              className="px-2 py-1 text-xs rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 bg-white"
                              title="Notes, action taken, and activity audit trail"
                            >
                              Notes
                            </button>
                            {canDeleteTask ? (
                              <button
                                type="button"
                                onClick={() => handleDeleteTask(item)}
                                className="px-2 py-1 text-xs rounded-md border border-red-300 text-red-700 hover:bg-red-50 bg-white inline-flex items-center"
                                title="Delete task (owner or admin)"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Delete
                              </button>
                            ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Enhanced Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedItems.length === 1 ? 'Assign Queue Item' : `Assign ${selectedItems.length} Queue Items`}
            </h3>
            
            {/* Show current assignment for single item */}
            {selectedItemDetails && selectedItemDetails.length === 1 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm font-medium text-gray-700 mb-2">Current Assignment:</p>
                <div className="flex items-center text-sm">
                  {(() => {
                    const item = selectedItemDetails[0];
                    const modalAt = assignFieldToString(item.assigned_to);
                    const assignedToType = item.assigned_to_type ||
                      (item.auto_assigned ? 'ai' : null) ||
                      (modalAt && modalAt.toLowerCase().includes('ai') ? 'ai' : 'human');
                    const assignedToName =
                      assignFieldToString(item.assigned_to_name) ||
                      modalAt ||
                      (item.auto_assigned ? 'Kai - AI Coworker' : 'Unassigned');
                    
                    return (
                      <>
                        {assignedToType === 'ai' ? (
                          <Bot className="w-4 h-4 text-blue-500 mr-2" />
                        ) : (
                          <User className="w-4 h-4 text-green-500 mr-2" />
                        )}
                        <span className="text-gray-900">{assignedToName}</span>
                        {toDisplayString(item.status, '') === 'in_progress' && (
                          <span className="ml-2 text-xs text-orange-600">(Work in progress will be preserved)</span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign To
                </label>
                <select
                  value={assignmentData.dropdownValue || assignmentData.assigned_to_id || (assignmentData.assigned_to_type === 'ai' ? 'ai' : assignmentData.assigned_to_type === 'unassigned' ? 'unassigned' : '')}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    
                    if (selectedValue === 'ai') {
                      setAssignmentData({ 
                        ...assignmentData, 
                        assigned_to_type: 'ai',
                        assigned_to_id: 'ai-agent-default',
                        assigned_to_name: 'Kai - AI Coworker',
                        dropdownValue: 'ai' });
                    } else if (selectedValue === 'unassigned') {
                      setAssignmentData({ 
                        ...assignmentData, 
                        assigned_to_type: 'unassigned',
                        assigned_to_id: '',
                        assigned_to_name: '',
                        dropdownValue: 'unassigned' });
                    } else if (selectedValue) {
                      const selectedUser = availableUsers.find(
                        (u) => getMemberAssigneeId(u) === selectedValue
                      );
                      if (selectedUser) {
                        setAssignmentData({ 
                          ...assignmentData, 
                          assigned_to_type: 'human',
                          assigned_to_id: selectedValue,
                          assigned_to_name: formatMemberDisplayName(selectedUser),
                          dropdownValue: selectedValue });
                      }
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  disabled={availableUsers.length === 0 && !assignmentData.dropdownValue}
                >
                  <option value="">Select assignee...</option>
                  {/* Team Members */}
                  {availableUsers.length > 0 ? (
                    availableUsers.map((user) => {
                      const userId = getMemberAssigneeId(user);
                      const userName = formatMemberDisplayName(user) || userId;
                      return (
                        <option key={userId || userName} value={userId}>
                          {userName}
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>Loading team members...</option>
                  )}
                  {/* AI Coworker */}
                  <option value="ai">Kai - AI Coworker</option>
                  {/* Unassigned */}
                  <option value="unassigned">Unassigned</option>
                </select>
                {availableUsers.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">Fetching team members from Team Management...</p>
                )}
              </div>

              {assignmentData.assigned_to_type !== 'unassigned' && (
                <>
                  {selectedItemDetails?.some(item => item.auto_assigned || item.assigned_to_type === 'ai') && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="override_ai"
                        checked={assignmentData.override_ai}
                        onChange={(e) => setAssignmentData({ ...assignmentData, override_ai: e.target.checked })}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="override_ai" className="ml-2 text-sm text-gray-700">
                        Override AI assignment
                      </label>
                    </div>
                  )}

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs text-blue-800">
                      <strong>Note:</strong> Work progress will be preserved when reassigning, even for items in progress.
                    </p>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Assignment (Optional)
                </label>
                <textarea
                  value={assignmentData.reason}
                  onChange={(e) => setAssignmentData({ ...assignmentData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="Enter reason for assignment..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowAssignmentModal(false);
                  setSelectedItems([]);
                  setSelectedItemDetails(null);
                  setAssignmentData({
                    assigned_to_type: 'human',
                    assigned_to_id: '',
                    assigned_to_name: '',
                    override_ai: false,
                    reason: '',
                    dropdownValue: '' });
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {selectedItems.length === 1 ? 'Assign' : 'Assign All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FilePlus className="w-5 h-5" />
              Create New Task
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="e.g. Review high-dollar denial"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                <select
                  value={createForm.source_service}
                  onChange={(e) => setCreateForm({ ...createForm, source_service: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="insurance_verification">Insurance Verification</option>
                  <option value="prior_authorization">Prior Authorization</option>
                  <option value="claims_processing">Claims Processing</option>
                  <option value="denial_management">Denial Management</option>
                  <option value="charge_capture">Charge Capture</option>
                  <option value="ar_posting">AR Posting</option>
                  <option value="remittance_advice">Remittance Advice</option>
                  <option value="patient_billing">Patient Billing</option>
                  <option value="contract_management">Contract Management</option>
                  <option value="rcm_compliance">RCM Compliance</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={createForm.priority}
                  onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
                <select
                  value={createForm.dropdownValue}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue === 'ai') {
                      setCreateForm({
                        ...createForm,
                        assigned_to_type: 'ai',
                        assigned_to_id: 'ai-agent-default',
                        assigned_to_name: 'Kai - AI Coworker',
                        dropdownValue: 'ai' });
                    } else if (selectedValue === 'unassigned') {
                      setCreateForm({
                        ...createForm,
                        assigned_to_type: 'unassigned',
                        assigned_to_id: '',
                        assigned_to_name: '',
                        dropdownValue: 'unassigned' });
                    } else {
                      const selectedUser = availableUsers.find(
                        (u) => getMemberAssigneeId(u) === selectedValue
                      );
                      setCreateForm({
                        ...createForm,
                        assigned_to_type: 'human',
                        assigned_to_id: selectedValue,
                        assigned_to_name: formatMemberDisplayName(selectedUser) || selectedValue,
                        dropdownValue: selectedValue });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="unassigned">Unassigned (Open)</option>
                  <option value="ai">Kai - AI Coworker</option>
                  {availableUsers.map((u) => {
                    const id = getMemberAssigneeId(u);
                    return (
                      <option key={id} value={id}>
                        {formatMemberDisplayName(u) || id}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="2"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                disabled={createBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateTask}
                disabled={createBusy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {createBusy ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes + Activity audit trail */}
      {showNotesModal && notesItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Task detail</h3>
                <p className="text-sm text-gray-500 mt-1">{getConsistentItemLabel(notesItem)}</p>
                <p className="text-xs text-gray-400 mt-0.5 capitalize">
                  {toDisplayString(notesItem.status, 'pending').replace(/_/g, ' ')}
                  {assignFieldToString(notesItem.assigned_to_name) || assignFieldToString(notesItem.assigned_to)
                    ? ` · ${assignFieldToString(notesItem.assigned_to_name) || assignFieldToString(notesItem.assigned_to)}`
                    : ' · Unassigned'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowNotesModal(false);
                  setNotesItem(null);
                  setActivityEvents([]);
                }}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                Close
              </button>
            </div>

            <div className="flex gap-2 border-b border-gray-200 mb-4">
              <button
                type="button"
                onClick={() => setNotesModalTab('activity')}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  notesModalTab === 'activity'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Activity / audit
              </button>
              <button
                type="button"
                onClick={() => setNotesModalTab('notes')}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  notesModalTab === 'notes'
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Work notes
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-[16rem]">
              {notesModalTab === 'activity' ? (
                <div className="space-y-0">
                  <p className="text-xs text-gray-500 mb-3">
                    Append-only lifecycle log: created → assigned → working → closed. Used for supervisor review and audit.
                  </p>
                  {activityLoading ? (
                    <p className="text-sm text-gray-500 py-8 text-center">Loading activity…</p>
                  ) : activityEvents.length === 0 ? (
                    <p className="text-sm text-gray-500 py-8 text-center">
                      No activity recorded yet. Open, assign, or update this task to start the trail.
                    </p>
                  ) : (
                    <ol className="relative border-l border-gray-200 ml-3 space-y-4">
                      {activityEvents.map((evt) => {
                        const tone =
                          evt.event_type === 'created'
                            ? 'bg-slate-500'
                            : evt.event_type === 'assigned' || evt.event_type === 'reassigned' || evt.event_type === 'auto_assigned'
                            ? 'bg-blue-500'
                            : evt.event_type === 'status_changed' && evt.to_status === 'completed'
                            ? 'bg-emerald-500'
                            : evt.event_type === 'status_changed' && evt.to_status === 'in_progress'
                            ? 'bg-amber-500'
                            : evt.event_type === 'notes_updated' || evt.event_type === 'action_taken'
                            ? 'bg-violet-500'
                            : 'bg-gray-400';
                        const when = evt.created_at
                          ? new Date(evt.created_at).toLocaleString()
                          : '';
                        return (
                          <li key={evt.id} className="ml-4">
                            <span
                              className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full ring-2 ring-white ${tone}`}
                            />
                            <div className="flex flex-wrap items-baseline justify-between gap-2">
                              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                {(evt.event_type || 'event').replace(/_/g, ' ')}
                              </span>
                              <span className="text-xs text-gray-400">{when}</span>
                            </div>
                            <p className="text-sm text-gray-900 mt-0.5">{evt.message}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {evt.actor_name || evt.actor_id || 'system'}
                              {evt.actor_type ? ` · ${evt.actor_type}` : ''}
                              {evt.from_status || evt.to_status
                                ? ` · ${evt.from_status || '—'} → ${evt.to_status || '—'}`
                                : ''}
                              {evt.from_assignee || evt.to_assignee
                                ? ` · assignee ${evt.from_assignee || '—'} → ${evt.to_assignee || '—'}`
                                : ''}
                            </p>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => loadItemActivity(notesItem)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={notesForm.notes}
                      onChange={(e) => setNotesForm({ ...notesForm, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows="4"
                      placeholder="Work notes (separate from the audit trail)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Action taken</label>
                    <textarea
                      value={notesForm.action_taken}
                      onChange={(e) => setNotesForm({ ...notesForm, action_taken: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      rows="2"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 mt-4 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setShowNotesModal(false);
                  setNotesItem(null);
                  setActivityEvents([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Close
              </button>
              {notesModalTab === 'notes' && (
                <button
                  type="button"
                  onClick={handleSaveNotes}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save notes
                </button>
              )}
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default RcmWorkQueueShell;
